import { useInfiniteQuery } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { timeTrackingService } from '../api/time-tracking.service';
import { TIME_TRACKING_KEYS } from './queryKeys';
import type { TimeEntryDto } from '../types';

const PAGE_SIZE = 10;

export interface InfiniteTimeEntriesResult {
  entries: TimeEntryDto[];
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

/**
 * Load-more (infinite) time entries for a task. Shares the `taskEntries` key
 * prefix so manual-entry mutations invalidate it.
 */
export function useTaskTimeEntriesInfinite(taskId: string | null): InfiniteTimeEntriesResult {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage: fetchQueryNextPage,
  } = useInfiniteQuery({
    queryKey: [...TIME_TRACKING_KEYS.taskEntries(orgId ?? '', taskId ?? ''), 'infinite'] as const,
    queryFn: ({ pageParam, signal }) =>
      timeTrackingService.getTaskTimeEntries(
        taskId!,
        { page: pageParam, pageSize: PAGE_SIZE },
        signal,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.page + 1 : undefined),
    enabled: !!orgId && !!taskId,
    staleTime: 120_000,
  });

  return {
    entries: data?.pages.flatMap((page) => page.items ?? []) ?? [],
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage: () => {
      void fetchQueryNextPage();
    },
  };
}
